import { Product, ProductVariant } from '../types';
import { languageKind } from './helpers';

/**
 * Раздел «Шеринг»: игра продается позицией на общем аккаунте — П2 или П3 (PS5),
 * у кросс-ген игр и подписок еще Т3 (PS4); у подписки «PS5» = П3, «PS4» = Т3.
 * Товар — обычная карточка с category = sharing, позиция — вариант с region = SH.
 */
export type SharingType = 'game' | 'game_crossgen' | 'subscription';
export type SharingPosition = 'P2' | 'P3' | 'T3';

export const POSITION_PLATFORM: Record<SharingPosition, string> = { P2: 'PS5', P3: 'PS5', T3: 'PS4' };
export const POSITION_LABEL: Record<SharingPosition, string> = { P2: 'П2', P3: 'П3', T3: 'Т3' };
const POSITION_ORDER: SharingPosition[] = ['P2', 'P3', 'T3'];

export const POSITION_HINT: Record<SharingPosition, string> = {
  P2: 'Играете на выданном аккаунте',
  P3: 'Играете на своем аккаунте',
  T3: 'Играете на своем аккаунте · PS4',
};

export const SHARING_TYPE_TITLE: Record<SharingType, string> = {
  game: 'Игра',
  game_crossgen: 'Игра · PS4 и PS5',
  subscription: 'Подписка',
};

export function sharingTypeOf(product: Pick<Product, 'sharingType'>): SharingType {
  return (product.sharingType as SharingType) || 'game';
}

export function isPosition(value: unknown): value is SharingPosition {
  return typeof value === 'string' && (POSITION_ORDER as string[]).includes(value);
}

/** Какие позиции бывают у типа: у игры нет Т3, у подписки нет П2 */
export const POSITIONS_BY_TYPE: Record<SharingType, SharingPosition[]> = {
  game: ['P2', 'P3'],
  game_crossgen: ['P2', 'P3', 'T3'],
  subscription: ['P3', 'T3'],
};

/** Позиции товара по порядку П2 → П3 → Т3; чужие для типа (остались после смены типа) не показываем */
export function sharingOffers(product: Product): ProductVariant[] {
  const allowed = POSITIONS_BY_TYPE[sharingTypeOf(product)];
  return (product.variants || [])
    .filter((v) => isPosition(v.position) && allowed.includes(v.position))
    .sort((a, b) => POSITION_ORDER.indexOf(a.position as SharingPosition) - POSITION_ORDER.indexOf(b.position as SharingPosition));
}

export function isLiveOffer(v: ProductVariant): boolean {
  return v.available && v.price > 0;
}

/** Самая дешевая живая позиция — цена для плитки */
export function cheapestOffer(product: Product): ProductVariant | null {
  const live = sharingOffers(product).filter(isLiveOffer);
  return live.length > 0 ? live.reduce((a, b) => (a.price <= b.price ? a : b)) : null;
}

/** Крупная подпись плитки позиции: «П2», «П3», «Т3» или консоль у подписки */
export function offerLabel(type: SharingType, v: ProductVariant): string {
  const pos = v.position as SharingPosition;
  return type === 'subscription' ? (POSITION_PLATFORM[pos] || v.platform || '') : POSITION_LABEL[pos];
}

export function offerHint(type: SharingType, v: ProductVariant): string {
  const pos = v.position as SharingPosition;
  if (type === 'subscription') return pos === 'T3' ? 'Подписка на PS4' : 'Подписка на PS5';
  return POSITION_HINT[pos];
}

/** Подпись позиции в корзине и заказе: «П3 (PS5)», у подписки — консоль */
export function positionCaption(product: Pick<Product, 'sharingType'>, v: Pick<ProductVariant, 'position' | 'platform'>): string {
  if (!isPosition(v.position)) return '';
  const platform = POSITION_PLATFORM[v.position] || v.platform || '';
  if (sharingTypeOf(product) === 'subscription') return platform;
  return platform ? `${POSITION_LABEL[v.position]} (${platform})` : POSITION_LABEL[v.position];
}

/** Короткая строка под названием плитки: что продается */
export function sharingKindLine(product: Product): string {
  const type = sharingTypeOf(product);
  const live = sharingOffers(product).filter(isLiveOffer);
  if (type === 'subscription') {
    const consoles = live.map((v) => POSITION_PLATFORM[v.position as SharingPosition]);
    return consoles.length > 0 ? `Подписка · ${consoles.join(' / ')}` : 'Подписка';
  }
  const labels = live.map((v) => POSITION_LABEL[v.position as SharingPosition]);
  return labels.length > 0 ? `Позиции ${labels.join(' · ')}` : 'Нет в наличии';
}

/* ─── Поиск и фильтры раздела — как в каталоге, без региона ─────────── */

export type SharingCategory = 'all' | 'games' | 'subscriptions';

export const SHARING_CATEGORIES: { value: SharingCategory; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'games', label: 'Игры' },
  { value: 'subscriptions', label: 'Подписки' },
];

export type SharingLanguage = 'ru_subtitles' | 'ru_full';

export const SHARING_LANGUAGES: { value: SharingLanguage; label: string }[] = [
  { value: 'ru_subtitles', label: 'Русские субтитры' },
  { value: 'ru_full', label: 'Полностью на русском' },
];

/** maxPrice = null — цена не ограничена (ползунок в крайнем правом положении) */
export interface SharingFilters {
  platforms: Array<'PS4' | 'PS5'>;
  languages: SharingLanguage[];
  maxPrice: number | null;
}

export const DEFAULT_SHARING_FILTERS: SharingFilters = { platforms: [], languages: [], maxPrice: null };

export function sharingFiltersCount(f: SharingFilters): number {
  return f.platforms.length + f.languages.length + (f.maxPrice !== null ? 1 : 0);
}

/** Верх ползунка цены: самая высокая цена «от», округленная вверх до сотни */
export function sharingPriceCeiling(products: Product[]): number {
  const max = products.reduce((m, p) => Math.max(m, cheapestOffer(p)?.price || 0), 0);
  return Math.max(100, Math.ceil(max / 100) * 100);
}

/** Раздел и фильтры; товар без живых позиций в выдачу не попадает */
export function applySharingFilters(products: Product[], category: SharingCategory, f: SharingFilters): Product[] {
  return products.filter((p) => {
    const type = sharingTypeOf(p);
    if (category === 'games' && type === 'subscription') return false;
    if (category === 'subscriptions' && type !== 'subscription') return false;
    const live = sharingOffers(p).filter(isLiveOffer);
    if (live.length === 0) return false;
    if (f.platforms.length > 0
      && !live.some((v) => (f.platforms as string[]).includes(POSITION_PLATFORM[v.position as SharingPosition]))) return false;
    if (f.languages.length > 0
      && !live.some((v) => { const k = languageKind(v.language as string | undefined); return !!k && f.languages.includes(k); })) return false;
    if (f.maxPrice !== null && Math.min(...live.map((v) => v.price)) > f.maxPrice) return false;
    return true;
  });
}
